// stores/projectGenerationStore.js
import { defineStore } from 'pinia';
let _beforeUnloadHandler = null;
let _allowNextNavigation = false;

// The controller for the run in flight.
//
// Deliberately module-level, NOT in Pinia state: an AbortController is not
// serialisable and Vue's reactive proxy would wrap it, so `signal` read back
// out of the store would be a Proxy that `fetch` refuses. Same class of trap as
// DiagramC's pending-message queue (see .claude/rules/diagrams.md).
let _abortController = null;

export const useProjectGenerationStore = defineStore('projectGeneration', {
  state: () => ({
    isGenerating: false,
    isMinimized: false,
    currentLoadingStep: 0,
    totalSteps: 0,
    loadingMessage: '',
    completedProjectId: null,
    generationFailed: false,
    // Outcome accounting, so a run that ran out of budget can say so instead of
    // reporting success over a half-built project.
    cardsCreated: 0,
    cardsExpected: 0,
    failedSteps: 0,
    outOfTokens: false,
    _routerGuardRemover: null,
    _router: null,
    _modalStore: null,
  }),

  getters: {
    progress: (state) => {
      if (!state.totalSteps) return 0;
      return Math.round(
        ((state.currentLoadingStep - 1) / state.totalSteps) * 100
      );
    },
    isActive: (state) =>
      state.isGenerating || state.completedProjectId !== null,

    /** True when the run finished but did not produce everything it planned. */
    isPartial: (state) =>
      state.completedProjectId !== null &&
      (state.failedSteps > 0 || state.outOfTokens),

    /**
     * What actually happened, in the user's terms.
     *
     * The old completion said "Project created successfully!" regardless — a
     * run that lost half its steps to an exhausted allowance reported the same
     * sentence as one that finished, and the only trace was a console warning.
     */
    outcomeMessage: (state) => {
      if (state.completedProjectId === null) return '';
      const made = state.cardsCreated;
      const planned = state.cardsExpected;
      const count =
        planned > 0 ? `${made} of about ${planned} cards` : `${made} cards`;

      if (state.outOfTokens) {
        return `Ran out of AI tokens — created ${count}. What was generated is saved; you can add the rest by hand.`;
      }
      if (state.failedSteps > 0) {
        return `Finished with ${state.failedSteps} step${
          state.failedSteps === 1 ? '' : 's'
        } skipped — created ${count}.`;
      }
      return 'Project created successfully!';
    },
  },

  actions: {
    // Call once from App.vue or a layout component on mount
    init(router, modalStore) {
      this._router = router;
      this._modalStore = modalStore;
    },
    _attachGuards() {
      if (typeof window === 'undefined') return;

      // Clear any stale listener first
      this._detachBeforeUnload();

      _beforeUnloadHandler = (e) => {
        if (!this.isGenerating) return;
        e.preventDefault();
        e.returnValue =
          'AI project generation is in progress. Leaving now will cancel it.';
        return e.returnValue;
      };
      window.addEventListener('beforeunload', _beforeUnloadHandler);

      if (this._router) {
        const removeGuard = this._router.beforeEach((_to, _from, next) => {
          if (_allowNextNavigation) {
            _allowNextNavigation = false;
            next();
            return;
          }
          if (!this.isGenerating || this.isMinimized) {
            next();
            return;
          }
          this._modalStore?.openConfirmationModal({
            title: 'Generation in Progress',
            message:
              'Your project is still being generated. Leaving now will cancel it.',
            confirmButtonLabel: '',
            declineButtonLabel: 'Leave & cancel',
            cancelButtonLabel: 'Stay here',
            showCloseButton: false,
            onDecline: () => {
              this.dismiss();
              next();
            },
            onCancel: () => next(false),
          });
        });
        this._routerGuardRemover = removeGuard;
      }
    },
    _detachBeforeUnload() {
      if (typeof window === 'undefined') return;
      if (_beforeUnloadHandler) {
        window.removeEventListener('beforeunload', _beforeUnloadHandler);
        _beforeUnloadHandler = null;
      }
    },

    _detachGuards() {
      this._detachBeforeUnload();
      if (this._routerGuardRemover) {
        this._routerGuardRemover();
        this._routerGuardRemover = null;
      }
    },
    allowNextNavigation() {
      _allowNextNavigation = true; // module-level, not this.*
    },

    /**
     * True while a run is in flight, for the double-click guard.
     *
     * Nothing stopped a second generation starting on top of the first: the
     * button stayed live, and a double-click bought two complete runs, both
     * billed, racing to write into the same tree. This is the client half of
     * that guard; it is not a security control (anyone can call the edge
     * functions directly) but it is what stops the accident.
     */
    isBusy() {
      return this.isGenerating;
    },

    /**
     * The signal to hand every functions.invoke() in this run.
     *
     * A method, not a getter: Pinia would expose a getter through the reactive
     * store proxy, and the AbortSignal read back out would be a Proxy that
     * fetch rejects with "parameter 1 is not of type 'AbortSignal'".
     */
    getSignal() {
      return _abortController?.signal;
    },

    /**
     * Count cards as they are created.
     *
     * Called from the file-tree store, which is the single choke point every
     * card insert already passes through — instrumenting the ~10 composable
     * creators instead would miss whichever one gets added next. A no-op unless
     * a generation is running, so ordinary card creation is not counted.
     */
    noteCardsCreated(n = 1) {
      if (!this.isGenerating) return;
      this.cardsCreated += n;
    },

    /** A step failed; recorded so completion can report an honest outcome. */
    noteStepFailed(n = 1) {
      this.failedSteps += n;
    },

    startGeneration(totalSteps, expectedCards = 0) {
      // Abandon any previous controller before minting a new one, so a run that
      // was never cleanly finished cannot leave listeners attached.
      _abortController?.abort();
      _abortController = new AbortController();

      this.cardsCreated = 0;
      this.cardsExpected = expectedCards;
      this.failedSteps = 0;
      this.outOfTokens = false;
      this.isGenerating = true;
      this.isMinimized = false;
      this.currentLoadingStep = 1;
      this.totalSteps = totalSteps;
      this.loadingMessage = 'Starting project creation...';
      this.completedProjectId = null;
      this.generationFailed = false;
      this._attachGuards();
    },

    /**
     * Stop the work, not just the UI.
     *
     * Cancelling used to set a flag that was checked BETWEEN steps, so the call
     * already in flight ran to completion on Cloudflare and was billed in full
     * — the user saw it stop, we kept paying. Aborting the signal closes the
     * HTTP connection, which fires `req.signal` inside the edge function, which
     * aborts the upstream model call.
     */
    abortRun() {
      _abortController?.abort();
      _abortController = null;
    },

    updateProgress(step, message) {
      this.currentLoadingStep = step;
      this.loadingMessage = message;
    },

    completeGeneration(projectId, { outOfTokens = false } = {}) {
      // Finished cleanly: drop the controller without aborting anything.
      _abortController = null;
      this.outOfTokens = outOfTokens;
      this.isGenerating = false;
      this.completedProjectId = projectId;
      this.currentLoadingStep = this.totalSteps + 1;
      // Reads the getter, so a partial run says what really happened.
      this.loadingMessage = this.outcomeMessage;
      this._detachGuards(); // remove beforeunload, keep pill visible
    },

    failGeneration() {
      this.abortRun();
      this.isGenerating = false;
      this.generationFailed = true;
      this.loadingMessage = 'Generation failed.';
      this._detachGuards();
    },

    minimize() {
      this.isMinimized = true;
    },

    expand() {
      this.isMinimized = false;
    },

    dismiss() {
      // Stop the in-flight work too. dismiss() is reached from the router
      // guard's 'Leave & cancel' and from the pill's close button, both of
      // which the user means as 'stop', not merely 'hide'.
      this.abortRun();
      this.isGenerating = false;
      this.isMinimized = false;
      this.currentLoadingStep = 0;
      this.totalSteps = 0;
      this.loadingMessage = '';
      this.completedProjectId = null;
      this.generationFailed = false;
      // Clear the outcome accounting too. `startGeneration` also resets these,
      // so leaving them was harmless — but it left a dismissed run's counts
      // readable, which is exactly the sort of stale state that later reads as
      // a live number.
      this.cardsCreated = 0;
      this.cardsExpected = 0;
      this.failedSteps = 0;
      this.outOfTokens = false;
      this._detachGuards();
    },

    setRouterGuardRemover(fn) {
      this._routerGuardRemover = fn;
    },

    _removeRouterGuard() {
      if (this._routerGuardRemover) {
        this._routerGuardRemover();
        this._routerGuardRemover = null;
      }
    },
  },
});
